import { motion, AnimatePresence } from "motion/react";
import { type PropsWithChildren, useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";

// Define more specific types for animation properties
type AnimationProps = {
  opacity?: number;
  x?: number;
  y?: number;
  scale?: number;
  rotate?: number;
};

type TransitionProps = {
  duration?: number;
  delay?: number;
  ease?: string | number[];
  type?: string;
};

interface AnimationWrapperProps {
  delay?: number;
  duration?: number;
  initial?: AnimationProps;
  animate?: AnimationProps;
  exit?: AnimationProps;
  transition?: TransitionProps;
}

export default function AnimationWrapper({
  children,
  delay = 1.0,
  duration = 0.3,
  initial = { opacity: 0 },
  animate = { opacity: 1 },
  exit = { opacity: 0 },
}: PropsWithChildren<AnimationWrapperProps>) {
  const [showContent, setShowContent] = useState(delay === 0);

  useEffect(() => {
    if (delay > 0) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, delay * 1000);

      return () => clearTimeout(timer);
    }
  }, [delay]);

  return (
    <AnimatePresence mode="wait">
      {!showContent ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}
        >
          <CircularProgress size={24} />
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={initial}
          animate={animate}
          exit={exit}
          transition={{ duration, delay: 0 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
